import { useEffect, useState } from 'react';
import { Download, X, Share, PlusSquare, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const DISMISS_KEY = 'install_banner_dismissed';
const isStandalone = () =>
  (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
  (typeof window !== 'undefined' && window.navigator.standalone === true);
const isIOS = () => typeof window !== 'undefined' && /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallBanner = () => {
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    const reveal = () => setTimeout(() => setShow(true), 1200);
    if (window.__deferredInstallPrompt) reveal();
    if (isIOS()) { setIos(true); reveal(); }
    const onReady = () => reveal();
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, '1'); };
    window.addEventListener('pwa-installable', onReady);
    window.addEventListener('pwa-installed', onInstalled);
    return () => { window.removeEventListener('pwa-installable', onReady); window.removeEventListener('pwa-installed', onInstalled); };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, '1'); };
  const install = async () => {
    const dp = window.__deferredInstallPrompt;
    if (dp) {
      dp.prompt();
      try { const { outcome } = await dp.userChoice; if (outcome === 'accepted') dismiss(); } catch (e) { /* noop */ }
      window.__deferredInstallPrompt = null;
    } else if (ios) {
      setIosOpen(true);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="md:hidden fixed inset-x-0 bottom-0 z-[200] p-3" data-testid="install-banner" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        <div className="mx-auto max-w-md rounded-2xl border border-border bg-card/95 backdrop-blur shadow-2xl p-3 flex items-center gap-3">
          <img src="/icons/icon-192.png" alt="Bodegon53" className="h-11 w-11 rounded-xl shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight">Instala nuestra app</p>
            <p className="text-xs text-muted-foreground leading-tight">Acceso rápido, sin navegador. Gratis.</p>
          </div>
          <Button size="sm" className="bg-brand-orange text-white hover:bg-brand-orange-dark font-semibold shrink-0" data-testid="install-banner-accept" onClick={install}><Download className="h-4 w-4" /> Instalar</Button>
          <button aria-label="Cerrar" data-testid="install-banner-dismiss" onClick={dismiss} className="grid place-items-center h-8 w-8 rounded-md text-muted-foreground hover:bg-accent shrink-0"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent data-testid="install-banner-ios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-brand-orange" /> Instalar en iPhone / iPad</DialogTitle>
            <DialogDescription>Añade Bodegon53 a tu pantalla de inicio desde Safari.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3"><span className="grid place-items-center h-7 w-7 shrink-0 rounded-full bg-brand-navy text-brand-cream text-xs font-bold">1</span><span className="flex items-center gap-1.5 pt-1">Toca <strong>Compartir</strong> <Share className="h-4 w-4 inline text-brand-orange" />.</span></li>
            <li className="flex items-start gap-3"><span className="grid place-items-center h-7 w-7 shrink-0 rounded-full bg-brand-navy text-brand-cream text-xs font-bold">2</span><span className="flex items-center gap-1.5 pt-1">Elige <strong>Añadir a pantalla de inicio</strong> <PlusSquare className="h-4 w-4 inline text-brand-orange" />.</span></li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallBanner;
